import {
  STROKE_SIZES,
  Vec,
  getPointsFromDrawSegments,
  getStrokePoints,
  type StrokeOptions,
  type StrokePoint,
  type TLDrawShape,
  type VecLike,
} from "tldraw";

/**
 * The student ink pipeline: raw pointer samples to the centreline that is
 * drawn, hit-tested and exported. STREAMLINE is how far the ink trails the
 * pointer; higher smooths tremor and lags more. LOOKAHEAD draws one point
 * this many samples past the pointer while the pen is down, to cancel that
 * lag; 0 turns it off. Both sit at tldraw's own feel today — the pipeline
 * stays so they can be tuned in one place.
 */
export const INK_STREAMLINE = 0.64;
export const INK_LOOKAHEAD = 0;

/** Plain ink: an unfilled, solid stroke the student drew. Closed strokes
 *  count too — tldraw flips `isClosed` on the live stroke near its start, and
 *  a renderer swap there would reshape the line under the pen. */
export function isPlainInk(shape: TLDrawShape): boolean {
  const { dash, fill } = shape.props;
  return dash === "solid" && fill === "none" && shape.meta?.role !== "agent";
}

/** Stroke width in page px, as tldraw computes it for a draw shape. */
export function inkStrokeWidth(shape: TLDrawShape): number {
  return (STROKE_SIZES[shape.props.size] + 1) * shape.props.scale;
}

function inkStrokeOptions(size: number, last: boolean): StrokeOptions {
  return {
    size,
    thinning: 0,
    streamline: INK_STREAMLINE,
    simulatePressure: false,
    last,
  };
}

/** The points plus one guess `samples` steps past the end, along the heading
 *  of the last two. Unchanged when there is no heading to follow. */
export function withLookahead(points: VecLike[], samples: number): VecLike[] {
  if (samples <= 0 || points.length < 2) return points;
  const last = Vec.From(points[points.length - 1]);
  const heading = Vec.Sub(last, points[points.length - 2]);
  if (heading.len() < 0.01) return points;
  return [...points, Vec.Add(last, heading.mul(samples))];
}

/**
 * The stroke's centreline: smoothed samples, plus the lookahead point while
 * the stroke is in progress. Geometry uses the same points, so a live hit
 * area reaches past the pen tip by the guess. Memoised per shape record:
 * geometry and render both ask, and tldraw issues a new record per sample.
 */
export function inkStrokePoints(shape: TLDrawShape): StrokePoint[] {
  const cached = strokePointsByShape.get(shape);
  if (cached) return cached;
  const points = computeInkStrokePoints(shape);
  strokePointsByShape.set(shape, points);
  return points;
}

const strokePointsByShape = new WeakMap<TLDrawShape, StrokePoint[]>();

function computeInkStrokePoints(shape: TLDrawShape): StrokePoint[] {
  const raw = getPointsFromDrawSegments(
    shape.props.segments,
    shape.props.scaleX,
    shape.props.scaleY,
  );
  // A shift-straight last segment already ends on the pointer, as in tldraw.
  const { segments } = shape.props;
  const showAsComplete =
    shape.props.isComplete ||
    segments[segments.length - 1]?.type === "straight";
  const points = showAsComplete ? raw : withLookahead(raw, INK_LOOKAHEAD);
  return getStrokePoints(
    points,
    inkStrokeOptions(inkStrokeWidth(shape), showAsComplete),
  );
}
